package acupoint.components

import antd.button.button
import antd.comment.comment
import antd.descriptions.descriptions
import antd.descriptions.descriptionsItem
import antd.form.form
import antd.form.formItem
import antd.input.input
import antd.input.textArea
import antd.list.ListComponent
import antd.list.list
import antd.message.message
import antd.modal.modal
import antd.pageheader.pageHeader
import antd.tabs.tabPane
import antd.tabs.tabs
import com.jingfangpai.common.data.model.IdName
import com.jingfangpai.common.orm.mapping.Comment
import com.jingfangpai.common.orm.mapping.enums.CommentEntityType
import com.jingfangpai.common.orm.mapping.enums.CommentType
import com.jingfangpai.web.components.ImageManager
import external.toJsObject
import kotlinext.js.Object
import kotlinext.js.js
import kotlinx.browser.window
import react.*
import react.dom.div
import react.dom.li
import react.router.dom.useHistory
import setTitle
import acupoint.containers.AcupointDetailPageContext
import antd.avatar.avatar
import antd.card.card
import antd.icon.editOutlined
import antd.icon.userOutlined
import com.jingfangpai.acupoint.model.AcupointDetail
import com.jingfangpai.common.data.type.DateTime
import kotlinx.html.style
import react.dom.jsStyle
import react.dom.span
import react.dom.svg
import urlBase
import user.service.AuthService

external interface AcupointDetailProps : RProps {
    var id: String
}

val AcupointInDetail = functionalComponent<AcupointDetailProps> { props ->

    val ctx = useContext(AcupointDetailPageContext)
    val history = useHistory()
    val (acupoint, setAcupoint) = useState(AcupointDetail())
    val (notes, setNotes) = useState(arrayOf<Comment>())
    val (showNoteModal, setShowNoteModal) = useState(false)
    val (editingNote, setEditingNote) = useState<Comment?>(null)
    val (noteForm, setNoteForm) = useState(Comment())
    val (saving, setSaving) = useState(false)
    val (saveError, setSaveError) = useState<String?>(null)
    val (acupointIdNames, setAcupointIdNames) = useState<Map<String, String>>(emptyMap())

    useEffect(props.id) {
        if (!props.id.isNullOrBlank()) {
            // Fetch all acupoint ID names for clickable text
            ctx.listIdAndName({ resp ->
                if (resp.ok() && resp.data != null) {
                    val idNames: Map<String, String> = resp.data!!.filter { it.id != null }
                        .associate { (it.name?:"") to (it.id?:"") }
                    setAcupointIdNames(idNames)
                }
            }, {})
            
            ctx.getDetail(props.id, { resp ->
                console.log("Acupoint detail response:", resp)
                console.log("Response ok:", resp.ok())
                console.log("Response data:", resp.data)
                console.log("Response message:", resp.message)
                
                if (resp.ok() && resp.data != null) {
                    try {
                        setAcupoint(resp.data!!)
                        setTitle(resp.data!!.name?.name ?: "穴位详情")
                        console.log("Successfully set acupoint:", resp.data!!)
                    } catch (e: Exception) {
                        console.error("Error setting acupoint:", e)
                        console.error("Error stack:", e.stackTraceToString())
                        message.error("解析穴位详情失败: ${e.message}")
                    }
                } else {
                    console.error("Failed to fetch acupoint detail:", resp)
                    message.error("获取穴位详情失败: ${resp.message ?: "未知错误"}")
                }
            }, { error ->
                console.error("Exception fetching acupoint detail:", error)
                console.error("Error stack:", error.stackTraceToString())
                val errorMsg = error.message ?: error.toString() ?: "未知错误"
                message.error("获取穴位详情失败: $errorMsg")
            })
            
            // Fetch notes for this acupoint
            ctx.getComments(CommentEntityType.Acupoint, props.id, CommentType.笔记, { resp ->
                if (resp.ok() && resp.data != null) {
                    setNotes(resp.data!!)
                }
            }, { error ->
                console.error("Failed to fetch notes:", error)
            })
        }
    }
    
    fun openAddNoteModal() {
        setEditingNote(null)
        setSaveError(null)
        setNoteForm(Comment().apply {
            entityType = CommentEntityType.Acupoint
            entityId = props.id
            type = CommentType.笔记
            authorId = AuthService.getUserAuthData()?.id ?: ""
            createdBy = AuthService.getUserAuthData()?.userName ?: ""
            weight = 0
        })
        setShowNoteModal(true)
    }
    
    fun openEditNoteModal(note: Comment) {
        setEditingNote(note)
        setSaveError(null)
        setNoteForm(Object.assign(Comment(), note))
        setShowNoteModal(true)
    }
    
    fun saveNote() {
        if (noteForm.comment.isNullOrBlank()) {
            setSaveError("请填写笔记内容")
            return
        }
        
        // Ensure entityType is Acupoint and set author info
        noteForm.entityType = CommentEntityType.Acupoint
        noteForm.entityId = props.id
        noteForm.type = CommentType.笔记
        noteForm.authorId = AuthService.getUserAuthData()?.id ?: ""
        
        // Set createdOn timestamp for new notes
        if (noteForm.createdOn == null) {
            noteForm.createdOn = DateTime()
        }
        
        setSaving(true)
        setSaveError(null)
        ctx.saveComment(noteForm, { resp ->
            setSaving(false)
            if (resp.ok()) {
                // Refresh notes list
                ctx.getComments(CommentEntityType.Acupoint, props.id, CommentType.笔记, { refreshResp ->
                    if (refreshResp.ok() && refreshResp.data != null) {
                        setNotes(refreshResp.data!!)
                    }
                    setShowNoteModal(false)
                    // Reset form after successful save
                    setNoteForm(Comment())
                    setEditingNote(null)
                }, {})
            } else {
                setSaveError("抱歉，出错了：${resp.message}")
            }
        }, {
            setSaving(false)
            console.log(it)
            setSaveError("保存失败，${it.message}")
        })
    }

    div("AcupointDetail") {
        pageHeader {
            attrs {
                className = "acupoint-detail-page-header-responsive"
                onBack = {
                    window.history.back()
                }
                val acupointName = acupoint.name?.name ?: ""
                val pinyin = acupoint.pinyin
                val titleWithPinyin = if (!pinyin.isNullOrBlank()) {
                    "$acupointName ($pinyin)"
                } else {
                    acupointName
                }
                title = titleWithPinyin
                extra = arrayOf(
                    buildElement {
                        button {
                            attrs {
                                type = "primary"
                                icon = buildElement { editOutlined {} }
                                onClick = {
                                    history.push("$urlBase/learning/acupoint/edit/${props.id}")
                                }
                            }
                            +"编辑穴位"
                        }
                    }
                )
            }

            div {
                
                // Image Manager Section
                ImageManager {
                    attrs {
                        entityType = com.jingfangpai.common.orm.mapping.enums.FileEntityType.Acupoint
                        entityId = props.id
                        onImagesChanged = { images ->
                            console.log("Images changed:", images.size)
                        }
                    }
                }
                
                // Basic Information Section
                card {
                    attrs {
                        title = "基本信息"
                        style = kotlinext.js.js {
                            marginBottom = "16px"
                        }
                    }
                    
                    descriptions {
                        attrs {
                            bordered = true
                            column = 2
                        }
                        
                        val location = acupoint.location
                        if (!location.isNullOrBlank()) {
                            descriptionsItem {
                                attrs {
                                    label = "定位"
                                    span = 2
                                }
                                +location
                            }
                        }
                        
                        val indications = acupoint.indications
                        if (!indications.isNullOrBlank()) {
                            descriptionsItem {
                                attrs {
                                    label = "主治"
                                    span = 2
                                }
                                +indications
                            }
                        }
                        
                        val operation = acupoint.operation
                        if (!operation.isNullOrBlank()) {
                            descriptionsItem {
                                attrs {
                                    label = "操作"
                                    span = 2
                                }
                                +operation
                            }
                        }

                        val experience = acupoint.experience
                        if (!experience.isNullOrBlank()) {
                            descriptionsItem {
                                attrs {
                                    label = "经验"
                                    span = 2
                                }
                                div {
                                    attrs["dangerouslySetInnerHTML"] = js { __html = experience.replace("\n", "<br>") }
                                }
                            }
                        }

                        val prescription = acupoint.prescription
                        if (!prescription.isNullOrBlank()) {
                            descriptionsItem {
                                attrs {
                                    label = "处方"
                                    span = 2
                                }
                                ClickableAcupointText {
                                    attrs.text = removeHtmlTags(prescription)
                                    attrs.acupointIdNames = acupointIdNames
                                    attrs.currentAcupointName = acupoint.name?.name
                                }
                            }
                        }

                        val differentiation = acupoint.differentiation
                        if (!differentiation.isNullOrBlank()) {
                            descriptionsItem {
                                attrs {
                                    label = "鉴别"
                                    span = 2
                                }
                                ClickableAcupointText {
                                    attrs.text = removeHtmlTags(differentiation)
                                    attrs.acupointIdNames = acupointIdNames
                                    attrs.currentAcupointName = acupoint.name?.name
                                }
                            }
                        }
                    }
                }
                
                // Other Acupoints from Same Meridian Section
                if (acupoint.meridianOthers.isNotEmpty()) {
                    card {
                        attrs {
                            title = "同经其他穴位"
                            style = kotlinext.js.js {
                                marginBottom = "16px"
                            }
                        }
                        
                        div {
                            attrs.jsStyle = kotlinext.js.js {
                                display = "flex"
                                flexWrap = "wrap"
                                gap = "8px"
                            }
                            
                            acupoint.meridianOthers.forEach { otherAcupoint ->
                                button {
                                    attrs {
                                        type = "link"
                                        style = kotlinext.js.js {
                                            padding = "4px 12px"
                                            fontSize = "14px"
                                        }
                                        onClick = {
                                            if (!otherAcupoint.id.isNullOrBlank()) {
                                                history.push("$urlBase/learning/acupoint/detail/${otherAcupoint.id}")
                                            }
                                        }
                                    }
                                    +(otherAcupoint.name?.name ?: "")
                                }
                            }
                        }
                    }
                }
                
                // Notes Section
                card {
                    attrs {
                        title = "笔记"
                    }
                    
                    div {
                        button {
                            attrs {
                                type = "primary"
                                style = kotlinext.js.js {
                                    marginBottom = "16px"
                                }
                                onClick = {
                                    openAddNoteModal()
                                }
                            }
                            +"添加笔记"
                        }
                        
                        if (notes.isEmpty()) {
                            div {
                                +"暂无笔记"
                            }
                        } else {
                            list<Comment, ListComponent<Comment>> {
                                attrs {
                                    className = "comment-list"
                                    header = "${notes.size}条笔记"
                                    itemLayout = "horizontal"
                                    dataSource = notes
                                    renderItem = { noteItem, index ->
                                        buildElement {
                                            li {
                                                comment {
                                                    attrs {
                                                        author = noteItem.createdBy

                                                        content = buildElement {
                                                            div {
                                                                div{
                                                                    if (!noteItem.title.isNullOrBlank()) {
                                                                        div {
                                                                            +noteItem.title!!
                                                                            attrs.jsStyle = kotlinext.js.js {
                                                                                display = "inline-block"
                                                                                fontWeight = "bold"
                                                                                marginBottom = "8px"
                                                                            }
                                                                        }
                                                                    }
                                                                    if (!noteItem.source.isNullOrBlank()) {
                                                                        div {
                                                                            +"（来源：${noteItem.source}）"
                                                                            attrs.jsStyle = kotlinext.js.js {
                                                                                display = "inline-block"
                                                                                marginTop = "8px"
                                                                                fontSize = "12px"
                                                                                color = "#666"
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                                div {
                                                                    +(noteItem.comment ?: "")
                                                                    attrs.jsStyle = kotlinext.js.js {
                                                                            whiteSpace = "pre-wrap"
                                                                        }

                                                                }
                                                            }
                                                        }
                                                        datetime = noteItem.createdOn?.toDateTimeString()
                                                        actions = arrayOf(
                                                            buildElement {
                                                                button {
                                                                    attrs {
                                                                        type = "link"
                                                                        size = "small"
                                                                        icon = buildElement {
                                                                            editOutlined {  }
                                                                        }
                                                                        onClick = {
                                                                            openEditNoteModal(noteItem)
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        )
                                                        attrs.avatar = buildElement {
                                                            avatar{
                                                                attrs {
                                                                    style = kotlinext.js.js {
                                                                        backgroundColor = "#87d068"
                                                                    }
                                                                    shape = "square"
                                                                    icon = buildElement {
                                                                        userOutlined {  }
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        
        // Note Modal
        modal {
            attrs {
                title = if (editingNote != null) "编辑笔记" else "添加笔记"
                visible = showNoteModal
                onCancel = {
                    if (!saving) {
                        setShowNoteModal(false)
                        // Reset form when closing modal
                        setNoteForm(Comment())
                        setEditingNote(null)
                        setSaveError(null)
                    }
                }
                onOk = {
                    saveNote()
                }
                okText = if (saving) "保存中..." else "保存"
                cancelText = "取消"
                okButtonProps = kotlinext.js.js {
                    loading = saving
                    disabled = saving
                }
            }
            
            form {
                attrs {
                    layout = "vertical"
                }
                
                // Show error message if any
                if (saveError != null) {
                    div {
                        attrs.style = kotlinext.js.js {
                                color = "#ff4d4f"
                                backgroundColor = "#fff2f0"
                                border = "1px solid #ffccc7"
                                padding = "8px 12px"
                                marginBottom = "16px"
                                borderRadius = "4px"
                            }

                        +saveError!!
                    }
                }
                
                formItem {
                    attrs {
                        label = "笔记标题"
                    }
                    input {
                        attrs {
                            value = noteForm.title ?: ""
                            placeholder = "请填写笔记标题（可选）"
                            disabled = saving
                            onChange = { event: dynamic ->
                                val newTitle = event.target.value as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        title = newTitle
                                    }
                                }
                            }

                        }
                    }
                }
                
                formItem {
                    attrs {
                        label = "笔记内容"
                    }
                    textArea {
                        attrs {
                            value = noteForm.comment ?: ""
                            rows = 8
                            placeholder = "请填写笔记内容"
                            disabled = saving
                            onChange = { event: dynamic ->
                                val newComment = event.target.value as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        comment = newComment
                                    }
                                }
                            }
                        }
                    }
                }
                
                formItem {
                    attrs {
                        label = "来源"
                    }
                    input {
                        attrs {
                            value = noteForm.source ?: ""
                            placeholder = "请填写笔记来源（可选）"
                            disabled = saving
                            onChange = { event: dynamic ->
                                val newSource = event.target.value as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        source = newSource
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
